import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { Button, Callout, Card, CardContent, CardGrid, CardHeader, DataTable, DataTableEmptyRow, Dialog, FormGrid, Select, StatusPill, TextField } from './ui'
import {
  chartOfAccountsApi,
  costCentresApi,
  dimensionOptionsApi,
  incomeCategoriesApi,
  type ChartAccount,
  type CostCentre,
  type DimensionOptionGroup,
  type IncomeCategory,
} from '../api/costs'
import { useFinanceDimensions } from '../lib/financeDimensions'

/**
 * R3-10 (flag finanza.dimensioni_estese) — scheda «Dimensioni e piano dei conti» del Controllo costi:
 * le liste configurabili di business unit e sedi (una lista vuota nasconde la dimensione ovunque), l'organico e le
 * dimensioni dei centri di costo (l'organico serve alla ripartizione «per dipendenti») e le categorie di ricavo ad
 * albero, che con quelle di spesa formano il piano dei conti gestionale. Nessun calcolo qui: tutto arriva dal server.
 */

function errMsg(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback
}

interface Props {
  centres: CostCentre[]
  writer: boolean
  onChanged: () => Promise<void>
}

export function ExtendedDimensionsTab({ centres, writer, onChanged }: Props) {
  return (
    <>
      <OptionLists writer={writer} />
      <CentreDimensions centres={centres} writer={writer} onChanged={onChanged} />
      <IncomeTree writer={writer} />
    </>
  )
}

/* ── Liste BU e sede ─────────────────────────────────────────────────────────────────────────── */

function OptionLists({ writer }: { writer: boolean }) {
  const [bu, setBu] = useState<DimensionOptionGroup>({ visible: false, items: [] })
  const [sites, setSites] = useState<DimensionOptionGroup>({ visible: false, items: [] })
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      const r = await dimensionOptionsApi.list(true)
      setBu(r.businessUnits)
      setSites(r.sites)
      setError('')
    } catch (e) {
      setError(errMsg(e, 'Errore durante il caricamento delle liste'))
    }
  }, [])
  useEffect(() => { void load() }, [load])

  return (
    <>
      {error && <Callout tone="error">{error}</Callout>}
      <CardGrid min={360}>
        <OptionList title="Business unit" kind="bu" group={bu} writer={writer} onChanged={load} onError={setError} />
        <OptionList title="Sedi" kind="sede" group={sites} writer={writer} onChanged={load} onError={setError} />
      </CardGrid>
    </>
  )
}

function OptionList({ title, kind, group, writer, onChanged, onError }: {
  title: string
  kind: 'bu' | 'sede'
  group: DimensionOptionGroup
  writer: boolean
  onChanged: () => Promise<void>
  onError: (m: string) => void
}) {
  const [name, setName] = useState('')

  async function add() {
    if (!name.trim()) return
    onError('')
    try {
      await dimensionOptionsApi.create(kind, name.trim())
      setName('')
      await onChanged()
    } catch (e) {
      onError(errMsg(e, 'Errore durante il salvataggio'))
    }
  }

  async function toggle(id: number, isActive: boolean) {
    onError('')
    try {
      await dimensionOptionsApi.update(id, { isActive: !isActive })
      await onChanged()
    } catch (e) {
      onError(errMsg(e, 'Errore durante l\'aggiornamento'))
    }
  }

  return (
    <Card variant="outlined" padding="none">
      <CardHeader
        title={title}
        subtitle={group.visible ? 'Il campo compare in spese, centri di costo e movimenti.' : 'Lista vuota: il campo è nascosto.'}
      />
      {writer && (
        <CardContent>
          <FormGrid columns={3} className="md-form-grid--end">
            <div className="md-span-2">
              <TextField label="Nuova voce" maxLength={100} value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div>
              <Button variant="tonal" fullWidth type="button" onClick={() => void add()}>Aggiungi</Button>
            </div>
          </FormGrid>
        </CardContent>
      )}
      <DataTable variant="plain">
        <thead><tr><th>Voce</th><th>Stato</th><th /></tr></thead>
        <tbody>
          {group.items.length === 0 && <DataTableEmptyRow colSpan={3} title="Nessuna voce" />}
          {group.items.map(o => (
            <tr key={o.id} className={o.isActive ? '' : 'text-muted'}>
              <td>{o.name}</td>
              <td><StatusPill tone={o.isActive ? 'success' : 'neutral'}>{o.isActive ? 'Attiva' : 'Disattiva'}</StatusPill></td>
              <td className="text-end">
                {writer && <Button variant="text" size="sm" onClick={() => void toggle(o.id, o.isActive)}>{o.isActive ? 'Disattiva' : 'Riattiva'}</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </Card>
  )
}

/* ── Dimensioni e organico dei centri di costo ───────────────────────────────────────────────── */

function CentreDimensions({ centres, writer, onChanged }: { centres: CostCentre[]; writer: boolean; onChanged: () => Promise<void> }) {
  const dims = useFinanceDimensions()
  const [editing, setEditing] = useState<CostCentre | null>(null)

  return (
    <Card variant="outlined" padding="none">
      <CardHeader
        title="Centri di costo: dimensioni e organico"
        subtitle="L'organico è la quota dei centri di destinazione nelle regole di ripartizione «per dipendenti»."
      />
      <DataTable variant="plain">
        <thead><tr><th>Centro</th><th>Business unit</th><th>Sede</th><th>Team</th><th className="text-end">Organico</th><th /></tr></thead>
        <tbody>
          {centres.length === 0 && <DataTableEmptyRow colSpan={6} title="Nessun centro di costo" />}
          {centres.map(c => (
            <tr key={c.id} className={c.isActive ? '' : 'text-muted'}>
              <td>{c.code} · {c.name}</td>
              <td>{dims.label('bu', c.businessUnitId) ?? '—'}</td>
              <td>{dims.label('sede', c.siteId) ?? '—'}</td>
              <td>{dims.label('team', c.teamId) ?? '—'}</td>
              <td className="text-end">{c.headcount ?? '—'}</td>
              <td className="text-end">
                {writer && <Button variant="text" size="sm" onClick={() => setEditing(c)}>Modifica</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      {editing && (
        <CentreDialog
          centre={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await onChanged() }}
        />
      )}
    </Card>
  )
}

function CentreDialog({ centre, onClose, onSaved }: { centre: CostCentre; onClose: () => void; onSaved: () => Promise<void> }) {
  const dims = useFinanceDimensions()
  const [bu, setBu] = useState(centre.businessUnitId ? String(centre.businessUnitId) : '')
  const [site, setSite] = useState(centre.siteId ? String(centre.siteId) : '')
  const [team, setTeam] = useState(centre.teamId ? String(centre.teamId) : '')
  const [heads, setHeads] = useState(centre.headcount != null ? String(centre.headcount) : '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Campo vuoto: se c'era un valore lo si toglie (0), altrimenti non si tocca.
  const idOrClear = (v: string, original?: number | null) => (v ? Number(v) : original ? 0 : undefined)

  async function save(e: FormEvent) {
    e.preventDefault()
    const headcount = heads.trim() === '' ? undefined : Number(heads)
    if (headcount !== undefined && (!Number.isInteger(headcount) || headcount < 0)) { setError('L\'organico deve essere un numero intero non negativo'); return }
    setSaving(true)
    setError('')
    try {
      await costCentresApi.update(centre.id, {
        businessUnitId: idOrClear(bu, centre.businessUnitId),
        siteId: idOrClear(site, centre.siteId),
        teamId: idOrClear(team, centre.teamId),
        headcount,
      })
      await onSaved()
    } catch (err) {
      setError(errMsg(err, 'Errore durante il salvataggio'))
      setSaving(false)
    }
  }

  const pick = (label: string, options: { id: number; label: string }[], value: string, set: (v: string) => void) => (
    <Select label={label} value={value} onChange={ev => set(ev.target.value)}>
      <option value="">Non assegnato</option>
      {value && !options.some(o => String(o.id) === value) && <option value={value}>#{value}</option>}
      {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
    </Select>
  )

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Dimensioni di ${centre.code}`}
      as="form"
      onSubmit={save}
      fullscreenOnCompact
      actions={(
        <>
          <Button variant="text" type="button" dismiss onClick={onClose}>Annulla</Button>
          <Button variant="text" type="submit" disabled={saving}>{saving ? 'Salvataggio…' : 'Salva'}</Button>
        </>
      )}
    >
      {error && <Callout tone="error">{error}</Callout>}
      <FormGrid>
        {dims.businessUnits.length > 0 && pick('Business unit', dims.businessUnits, bu, setBu)}
        {dims.sites.length > 0 && pick('Sede', dims.sites, site, setSite)}
        {pick('Team', dims.teams, team, setTeam)}
        <TextField label="Organico (dipendenti)" type="number" min={0} value={heads} onChange={e => setHeads(e.target.value)} />
      </FormGrid>
    </Dialog>
  )
}

/* ── Categorie di ricavo e piano dei conti ───────────────────────────────────────────────────── */

function flatten<T extends { id: number; parentId?: number | null }>(items: T[]): { node: T; depth: number }[] {
  const byParent = new Map<number | null, T[]>()
  for (const i of items) {
    const key = i.parentId != null && items.some(x => x.id === i.parentId) ? i.parentId : null
    byParent.set(key, [...(byParent.get(key) ?? []), i])
  }
  const out: { node: T; depth: number }[] = []
  const walk = (parent: number | null, depth: number) => {
    for (const n of byParent.get(parent) ?? []) {
      out.push({ node: n, depth })
      walk(n.id, depth + 1)
    }
  }
  walk(null, 0)
  return out
}

function IncomeTree({ writer }: { writer: boolean }) {
  const [items, setItems] = useState<IncomeCategory[]>([])
  const [chart, setChart] = useState<{ expenses: ChartAccount[]; income: ChartAccount[] }>({ expenses: [], income: [] })
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [parent, setParent] = useState('')
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      const [list, plan] = await Promise.all([incomeCategoriesApi.list(true), chartOfAccountsApi.get()])
      setItems(list)
      setChart(plan)
      setError('')
    } catch (e) {
      setError(errMsg(e, 'Errore durante il caricamento delle categorie di ricavo'))
    }
  }, [])
  useEffect(() => { void load() }, [load])

  const rows = useMemo(() => flatten(items), [items])

  async function run(action: () => Promise<unknown>, fallback: string) {
    setError('')
    try {
      await action()
      await load()
    } catch (e) {
      setError(errMsg(e, fallback))
    }
  }

  async function add() {
    if (!name.trim()) { setError('Il nome è obbligatorio'); return }
    await run(async () => {
      await incomeCategoriesApi.create({
        name: name.trim(),
        parentId: parent ? Number(parent) : undefined,
        externalAccountCode: code.trim() || undefined,
      })
      setName('')
      setCode('')
      setParent('')
    }, 'Errore durante il salvataggio')
  }

  return (
    <CardGrid min={360}>
      <Card variant="outlined" padding="none">
        <CardHeader title="Categorie di ricavo (massimo 3 livelli)" />
        {error && <Callout tone="error" className="m-2">{error}</Callout>}
        {writer && (
          <CardContent>
            <FormGrid columns={3} className="md-form-grid--end">
              <div className="md-span-2"><TextField label="Nome" maxLength={100} value={name} onChange={e => setName(e.target.value)} /></div>
              <TextField label="Codice conto" maxLength={50} value={code} onChange={e => setCode(e.target.value)} />
              <div className="md-span-2">
                <Select label="Sotto" value={parent} onChange={e => setParent(e.target.value)}>
                  <option value="">— radice —</option>
                  {items.filter(i => i.isActive).map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                </Select>
              </div>
              <div><Button variant="tonal" fullWidth type="button" onClick={() => void add()}>Aggiungi</Button></div>
            </FormGrid>
          </CardContent>
        )}
        <DataTable variant="plain">
          <thead><tr><th>Categoria</th><th>Codice</th><th>Sotto</th><th /></tr></thead>
          <tbody>
            {rows.length === 0 && <DataTableEmptyRow colSpan={4} title="Nessuna categoria di ricavo" />}
            {rows.map(({ node, depth }) => (
              <tr key={node.id} className={node.isActive ? '' : 'text-muted'}>
                <td style={{ paddingInlineStart: `${16 + depth * 20}px` }}>{node.name}</td>
                <td>{node.externalAccountCode ?? '—'}</td>
                <td>
                  <Select
                    label={`Categoria padre di ${node.name}`} labelHidden disabled={!writer}
                    value={node.parentId ?? ''}
                    onChange={e => void run(() => incomeCategoriesApi.setParent(node.id, e.target.value ? Number(e.target.value) : null), 'Errore durante lo spostamento')}
                  >
                    <option value="">— radice —</option>
                    {items.filter(o => o.id !== node.id).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </Select>
                </td>
                <td className="text-end">
                  {writer && (
                    <Button variant="text" size="sm"
                      onClick={() => void run(() => incomeCategoriesApi.update(node.id, { isActive: !node.isActive }), 'Errore durante l\'aggiornamento')}>
                      {node.isActive ? 'Disattiva' : 'Riattiva'}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </Card>

      <Card variant="outlined" padding="none">
        <CardHeader title="Piano dei conti gestionale" subtitle="Categorie di spesa e di ricavo, con il codice del piano dei conti esterno." />
        <DataTable variant="plain">
          <thead><tr><th>Tipo</th><th>Voce</th><th>Codice</th></tr></thead>
          <tbody>
            {[...flatten(chart.expenses), ...flatten(chart.income)].length === 0 && <DataTableEmptyRow colSpan={3} title="Nessuna voce" />}
            {flatten(chart.income).map(({ node, depth }) => (
              <tr key={`r${node.id}`}>
                <td><StatusPill tone="success">Ricavo</StatusPill></td>
                <td style={{ paddingInlineStart: `${16 + depth * 20}px` }}>{node.name}</td>
                <td>{node.externalAccountCode ?? '—'}</td>
              </tr>
            ))}
            {flatten(chart.expenses).map(({ node, depth }) => (
              <tr key={`s${node.id}`}>
                <td><StatusPill tone="neutral">Spesa</StatusPill></td>
                <td style={{ paddingInlineStart: `${16 + depth * 20}px` }}>{node.name}</td>
                <td>{node.externalAccountCode ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </Card>
    </CardGrid>
  )
}
